import { EntityEnvironment, bridge } from "@dinahmoe/dmafjs-core";
import { browserRuntime } from "@dinahmoe/dmafjs-web";

/**
 * Browser environment for Navigation component
 *
 * Handles:
 * - Browser history API (pushState, replaceState, popstate)
 * - URL manipulation (pathname, hash, query params)
 * - Browser back/forward button support
 */
class NavigationEnv extends EntityEnvironment {
  #nodes = new Map();

  constructor(...args) {
    super(...args);
    browserRuntime.registerEngine("navigation", {
      create: (message) => this.#onCreate(message),
      update: (message) => this.#onUpdate(message),
      destroy: (message) => this.#onDestroy(message),
    });
  }

  #onCreate(message) {
    const node = { uid: message.uid, namespace: message.namespace, props: message.props };
    this.#nodes.set(message.uid, node);
    window.addEventListener("popstate", () => this._onPopStateChange(node));
    this._onPopStateChange(node);
  }

  #onUpdate(message) {
    const node = this.#nodes.get(message.uid);
    if (!node) return;
    const { key, value } = message;
    if (typeof this[key] === "function") {
      this[key](node, value);
    }
  }

  #onDestroy(message) {
    this.#nodes.delete(message.uid);
  }

  _onPopStateChange(node) {
    bridge.update({
      uid: node.uid,
      key: "location",
      value: {
        title: document.title,
        pathname: location.pathname,
        hash: location.hash,
        href: location.href,
        queryParams: Object.fromEntries(new URLSearchParams(location.search)),
      },
      sender: "browser",
    });
  }

  setNavigationState(node, config) {
    const {
      pathname = "",
      title = "",
      hash = "",
      queryParams = {},
      href = "",
      replace = false,
    } = config;

    const url = new URL(href || window.location.origin);

    if (pathname) url.pathname = pathname;
    if (hash) url.hash = hash;
    if (title) document.title = title;
    Object.keys(queryParams).forEach((key) => url.searchParams.set(key, queryParams[key]));

    if (Object.keys(queryParams).length === 0) {
      url.search = "";
    }

    const urlString = url.toString();
    if (urlString === window.location.href && !replace) {
      return;
    }

    if (replace) {
      history.replaceState({}, title, urlString);
    } else {
      history.pushState({}, title, urlString);
    }

    // Notify the core about the URL change
    this._onPopStateChange(node);
  }

  openInNewTab(node, config) {
    const { url } = config;
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

const navigationEnv = new NavigationEnv("navigation:controller");
export { navigationEnv, NavigationEnv };
